/**
 * Group Order Vuex module.
 * Calls /owa/group-orders endpoints.
 *
 * LocalStorage keys (go_ prefix):
 *   go_inviteUuid, go_participantCartOrderId, go_role
 *
 * OCC versions are NOT stored in localStorage — they come from server responses
 * and are held in Vuex state only. On page refresh, restoreSession re-fetches them.
 */

import Vue from 'vue'
import { GroupOrderWebSocketService } from '@/services/group-order-ws.service'
import { loadStripe } from '@stripe/stripe-js'
import { endSentence } from '@/services/util.service'

// Module-level WS singleton — not in Vuex state so it's not reactive
var _wsInstance = null
// Debounce timer for batching guest change notifications
var _notificationTimer = null
// Shared across concurrent fetchGroupState() calls targeting the SAME invite+cart --
// see the comment on the fetchGroupState action for why this exists. Keyed (not a
// single shared slot) because restoreSession/recoverGroupOrder can pass an explicit
// inviteUuid/participantCartOrderId for a DIFFERENT session than whatever's currently
// active in state, and that must never be collapsed with an unrelated in-flight call.
var _inFlightFetchGroupState = {}

// ---------------------------------------------------------------------------
// Helpers
// Parses a concatenated sub-order specialRequest string (built by buildGroupOrderSpecialRequest in cart.js)
// Supports two formats:
//   New: "uuid|note\nuuid2|note2"
//   Legacy: "Item Name: note\nItem Name 2: note2"
function parseItemSpecialRequest(subSpecialRequest, itemUuid, itemName) {
  if (!subSpecialRequest) return ''
  var lines = subSpecialRequest.split('\n')
  // Try uuid-based format first
  if (itemUuid) {
    var uuidPrefix = itemUuid + '|'
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].indexOf(uuidPrefix) === 0) {
        return lines[i].slice(uuidPrefix.length)
      }
    }
  }
  // Fall back to name-based format
  if (itemName) {
    var namePrefix = itemName + ': '
    for (var j = 0; j < lines.length; j++) {
      if (lines[j].indexOf(namePrefix) === 0) {
        return lines[j].slice(namePrefix.length)
      }
    }
  }
  return ''
}

// ---------------------------------------------------------------------------

var LS_KEYS = {
  inviteUuid: 'go_inviteUuid',
  participantCartOrderId: 'go_participantCartOrderId',
  role: 'go_role',
  idempotencyKey: 'go_checkoutIdempotencyKey',
  gatewayPaymentId: 'go_checkoutGatewayPaymentId',
  scheduledTime: 'go_scheduledTime',
  scheduledDay: 'go_scheduledDay',
  pendingLeave: 'go_pendingLeave',
}

function lsGet(key) {
  try { return window.localStorage.getItem(key) } catch (e) { return null }
}
function lsSet(key, value) {
  try { window.localStorage.setItem(key, value) } catch (e) { /* quota */ }
}
function lsRemove(key) {
  try { window.localStorage.removeItem(key) } catch (e) { /* noop */ }
}

function persistSession(inviteUuid, participantCartOrderId, role) {
  lsSet(LS_KEYS.inviteUuid, inviteUuid)
  lsSet(LS_KEYS.participantCartOrderId, participantCartOrderId)
  lsSet(LS_KEYS.role, role)
}

function clearSession() {
  Object.entries(LS_KEYS).forEach(function(entry) {
    if (entry[0] !== 'pendingLeave') lsRemove(entry[1])
  })
}

// Apply group order's locked settings (orderType, deliveryAddress, scheduledTime) onto the regular cart store.
// Called after createGroup, joinGroup, and restoreSession so the cart always reflects the group.
function applyGroupSettingsToCart(store, groupState) {
  if (!groupState) return
  var group = groupState.group || groupState
  var orderType = group.orderType
  if (orderType) {
    store.commit('SET_ORDER_TYPE', orderType)
  }
  var deliveryAddress = group.deliveryAddress
    || (groupState.participantCart && groupState.participantCart.deliveryAddress)
  if (deliveryAddress) {
    store.commit('SET_DELIVERY_ADDRESS', deliveryAddress)
    var formatted = [
      deliveryAddress.addressLine1,
      deliveryAddress.city,
      deliveryAddress.province,
      deliveryAddress.country
    ].filter(Boolean).join(', ')
    store.commit('SET_DELIVERY_ADDRESS_RESULT', { formatted_address: formatted })
  }
  var rawScheduledTime = group.scheduledTime
    || (groupState.participantCart && groupState.participantCart.scheduledTime)
  // Treat 'now' string as ASAP — it's not a real ISO time
  var scheduledTime = (rawScheduledTime && rawScheduledTime !== 'now') ? rawScheduledTime : null
  var currentCheckoutInfo = (store.state.checkout && store.state.checkout.checkoutInfo) || {}
  if (scheduledTime) {
    store.commit('SET_CHECKOUT_INFO', Object.assign({}, currentCheckoutInfo, {
      scheduledTime: scheduledTime,
      scheduledDay: scheduledTime,
    }))
    lsSet(LS_KEYS.scheduledTime, scheduledTime)
  } else {
    // ASAP — set scheduledTime: 'now' and scheduledDay to today's date so the date displays
    store.commit('SET_CHECKOUT_INFO', Object.assign({}, currentCheckoutInfo, {
      scheduledTime: 'now',
      scheduledDay: new Date().toISOString(),
    }))
  }
}

// Undo group order's locked settings after session expires or is cancelled.
function resetCartSettings(store) {
  store.commit('SET_DELIVERY_ADDRESS', null)
  store.commit('SET_DELIVERY_ADDRESS_RESULT', null)
  store.commit('SET_ORDER_TYPE', 'PICKUP')
  var currentCheckoutInfo = (store.state.checkout && store.state.checkout.checkoutInfo) || {}
  var cleaned = Object.assign({}, currentCheckoutInfo)
  delete cleaned.scheduledTime
  delete cleaned.scheduledDay
  store.commit('SET_CHECKOUT_INFO', cleaned)
  // Re-apply default schedule (ASAP or earliest available) same as app initialization
  store.dispatch('setDefaultCheckoutInfoIfEmpty')
}

// True if any item already carries a non-empty .errors from a previous stamp -- the one
// case where a cart with no current errors still needs rebuilding (to strip them).
function cartHasStampedItemErrors(cart) {
  var subOrderList = (cart && cart.subOrderList) || []
  for (var i = 0; i < subOrderList.length; i++) {
    var orderItemList = subOrderList[i].orderItemList || []
    for (var j = 0; j < orderItemList.length; j++) {
      var itemErrors = orderItemList[j].errors
      if (itemErrors && itemErrors.length) return true
    }
  }
  return false
}

// Stamps each cart-level validationError onto the specific item(s) it names (matched by
// name-as-prefix of the error's message -- see SET_PARTICIPANT_CART_VALIDATION_ERRORS
// below for why: the backend doesn't populate item.errors itself, and its
// OrderValidationError.cause, the actual item reference, is @JsonIgnore'd, so text
// matching is the only correlation available). Shared by SET_GROUP_STATE (every GET/
// refresh) and SET_PARTICIPANT_CART_VALIDATION_ERRORS (immediately after a failed
// mutation) so the per-item highlight survives a page refresh too, not just the
// in-session mutation error.
function stampItemErrors(cart) {
  if (!cart) return cart
  var errors = cart.validationErrors || []
  if (!cart.subOrderList) return cart
  // Fast path: with no validation errors there is nothing to stamp, so return the cart
  // as-is rather than deep-cloning every subOrder and item into fresh objects. This runs
  // on EVERY SET_GROUP_STATE -- and a host receives one per CART_CHANGED from every guest
  // (group-order-ws.service.js routes all participants' events to the host), each
  // possibly retried up to 5x by the version guard. Cloning made every one of those a
  // whole-tree rebuild that Vue 2 then had to re-observe, which in turn re-fired the
  // deep watchers on participantCart(s) in Cart.vue -- the bulk of the host-side lag
  // (DEV-11114). The error case below is unchanged; it just no longer pays for the
  // common no-error case.
  //
  // Only safe to skip when nothing was stamped before: SET_GROUP_STATE always passes a
  // fresh API payload (no prior stamp to go stale), but
  // SET_PARTICIPANT_CART_VALIDATION_ERRORS re-stamps the CURRENT cart, which may still
  // carry item.errors from an earlier failure. Returning early there would leave those
  // highlights on screen after the errors cleared, so fall through and strip them.
  if (!errors.length && !cartHasStampedItemErrors(cart)) return cart
  var subOrderList = cart.subOrderList.map(function(so) {
    var orderItemList = ((so.orderItemList) || []).map(function(item) {
      var itemErrors = errors.filter(function(err) {
        return item.name && err.errorMsg && err.errorMsg.indexOf(item.name) === 0
      })
      return Object.assign({}, item, { errors: itemErrors })
    })
    return Object.assign({}, so, { orderItemList: orderItemList })
  })
  return Object.assign({}, cart, { subOrderList: subOrderList })
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

function state() {
  return {
    sessionGeneration: 0,
    inviteUuid: null,
    participantCartOrderId: null,
    group: null,
    participantCart: null,
    participantCarts: [],
    isHost: false,
    joinAllowed: true,
    checkoutEligible: false,
    checkoutBlockedReasons: [],
    blockingParticipantCartOrderIds: [],
    wsConnected: false,
    checkoutStatus: null,
    isLoading: false,
    error: null,
    removed: false,
    left: false,
    leftGroupName: null,
    cancelled: false,
    recoveredSession: null,
    groupChangedNotification: null,
  }
}

// ---------------------------------------------------------------------------
// Getters
// ---------------------------------------------------------------------------

var getters = {
  isInGroupOrder: function(state) {
    return !!(state.inviteUuid && state.group)
  },
  isGuestParticipant: function(state) {
    return !!(state.inviteUuid && state.group && !state.isHost && state.participantCart && state.participantCart.guestName)
  },
  myParticipantCartOrderId: function(state) {
    return state.participantCartOrderId
  },
  groupVersion: function(state) {
    return state.group ? state.group.version : null
  },
  groupOrderNotes: function(state) {
    return state.group ? state.group.orderNotes : null
  },
  cartVersion: function(state) {
    return state.participantCart ? state.participantCart.version : null
  },
  groupStatus: function(state) {
    return state.group ? state.group.status : null
  },
  myCartStage: function(state) {
    return state.participantCart ? state.participantCart.stage : null
  },
  myCartLocked: function(state) {
    return state.participantCart ? state.participantCart.locked : false
  },
  myCartLockedReason: function(state) {
    return state.participantCart ? state.participantCart.lockedReason : null
  },
  // Same shape/formatting as cart.js's cartValidationErrors -- the host's participant
  // cart carries validationErrors the same way the normal (non-group) cart does, so this
  // feeds the same cartErrorMessage banner instead of a separate group-only display path.
  // Appends the same "please remove it" instruction cart.js's version does -- a group
  // order's mutation can silently no-op on an UNRELATED item add while a different item
  // is the one actually blocking it, so the fix (not just the problem) needs to be named.
  //
  // Filters out ORD_ERR_0027/ORD_ERR_0039 (delivery/pickup minimum-order-value), same as
  // cart.js's own cartValidationErrors already does for a group order's host cart --
  // minimumOrderValueInCents is an ORDER-level setting (the group's combined total), not
  // per-participant, so a guest whose own slice is under it must never see this as an
  // item-level error (Cart.vue's displayedCartErrorMessage/goMarkReady route the guest
  // banner and ready-button gate through this exact getter). Defense-in-depth: the
  // backend (OrderAPI.checkGeneralOrderEligiable) no longer stamps these codes onto a
  // ParticipantCart at all, but this getter shouldn't assume that's deployed everywhere
  // it runs, and cart.js's sibling getter already filters the same codes for the host.
  cartValidationErrors: function(state) {
    var errors = (state.participantCart && state.participantCart.validationErrors) || []
    errors = errors.filter(function(error) { return error.errorCode !== 'ORD_ERR_0027' && error.errorCode !== 'ORD_ERR_0039' })
    var suffix = Vue.prototype.$vuetify.t('$vuetify.cart.removeInvalidItemToContinue')
    return errors.map(function(error) { return endSentence(error.message) + ' ' + suffix }).join(',<br>')
  },
  // Host-only signal: any OTHER participant's cart (not just my own, which
  // cartValidationErrors above already covers) has a validation error -- e.g. a guest's
  // item went out of stock. The backend's own checkoutEligible/checkoutBlockedReasons
  // (evaluateCheckoutEligibility) never checks this either, only lock/limit/empty state,
  // so without this the host's "Go to Checkout" stays fully enabled while a guest's
  // out-of-stock item sits unresolved (visible in the "Others in your group" panel, but
  // not blocking). participantCarts already carries validationErrors for every member
  // when the caller is the host (GroupOrderService.buildState).
  anyParticipantCartHasErrors: function(state) {
    return (state.participantCarts || []).some(function(pc) {
      return pc.stage !== 'REMOVED' && Array.isArray(pc.validationErrors) && pc.validationErrors.length > 0
    })
  },
  myCartItems: function(state) {
    if (!state.participantCart || !state.participantCart.subOrderList) return []
    var items = []
    state.participantCart.subOrderList.forEach(function(so) {
      var subSpecialRequest = so.specialRequest || ''
      if (so.orderItemList) {
        so.orderItemList.forEach(function(item) {
          // item.specialRequest is the real per-item field (OrderItem has its own native
          // field on the backend). The sub-order-level parse is only a fallback for orders
          // placed before per-item notes were set directly, where the uuid-packed legacy
          // format is the only place the note exists.
          var itemNote = item.specialRequest || parseItemSpecialRequest(subSpecialRequest, item.uuid, item.name)
          items.push(Object.assign({}, item, { specialRequest: itemNote }))
        })
      }
    })
    return items
  },
  myCartSubTotal: function(state) {
    return state.participantCart ? (state.participantCart.subTotal || 0) : 0
  },
  myCartTotal: function(state) {
    return state.participantCart ? (state.participantCart.total || 0) : 0
  },
  groupSubTotal: function(state) {
    if (!state.participantCarts || !state.participantCarts.length) {
      return state.participantCart ? (state.participantCart.subTotal || 0) : 0
    }
    return state.participantCarts
      .filter(function(pc) { return pc.stage !== 'REMOVED' && pc.stage !== 'COMPLETED' })
      .reduce(function(sum, pc) { return sum + (pc.subTotal || 0) }, 0)
  },
  groupTotal: function(state) {
    if (!state.participantCarts || !state.participantCarts.length) {
      return state.participantCart ? (state.participantCart.total || 0) : 0
    }
    return state.participantCarts
      .filter(function(pc) { return pc.stage !== 'REMOVED' && pc.stage !== 'COMPLETED' })
      .reduce(function(sum, pc) { return sum + (pc.total || 0) }, 0)
  },
  groupPreDiscountTotal: function(state) {
    if (!state.participantCarts || !state.participantCarts.length) {
      var pc = state.participantCart
      if (!pc) return 0
      return (pc.total || 0) - (pc.discountTotal || 0) - (pc.deliveryCharge || 0)
    }
    return state.participantCarts
      .filter(function(pc) { return pc.stage !== 'REMOVED' && pc.stage !== 'COMPLETED' })
      .reduce(function(sum, pc) { return sum + (pc.total || 0) - (pc.discountTotal || 0) - (pc.deliveryCharge || 0) }, 0)
  },
  groupTaxTotal: function(state) {
    if (!state.participantCarts || !state.participantCarts.length) {
      return state.participantCart ? (state.participantCart.taxTotal || 0) : 0
    }
    return state.participantCarts
      .filter(function(pc) { return pc.stage !== 'REMOVED' && pc.stage !== 'COMPLETED' })
      .reduce(function(sum, pc) { return sum + (pc.taxTotal || 0) }, 0)
  },
  groupDeliveryCharge: function(state) {
    if (!state.participantCart) return null
    const charge = state.participantCart.deliveryCharge
    return (charge === null || charge === undefined) ? null : charge
  },
  spendingLimit: function(state) {
    return state.group ? state.group.maxPreDiscountAmount : null
  },
  countdownEndsAt: function(state) {
    return state.group ? state.group.countdownEndsAt : null
  },
  groupName: function(state) {
    return state.group ? state.group.groupName : null
  },
  myGuestName: function(state) {
    return state.participantCart ? state.participantCart.guestName : null
  },
  allParticipantCarts: function(state) {
    return state.participantCarts
  },
  inviteLink: function(state) {
    if (!state.inviteUuid) return null
    var base = window.location.href.split('?')[0].replace(/\/+$/, '')
    return base + '?grp=' + state.inviteUuid
  },
  legacyGroupOrderSession: function(state, getters, rootState) {
    if (!state.group || !state.participantCart) return null
    var myItems = []
    if (state.participantCart.subOrderList) {
      state.participantCart.subOrderList.forEach(function(so) {
        if (so.orderItemList) myItems = myItems.concat(so.orderItemList)
      })
    }
    var members = state.participantCarts.filter(function(pc) {
      return pc.stage !== 'REMOVED' && pc.stage !== 'COMPLETED' && pc.orderId !== state.participantCartOrderId
    }).map(function(pc) {
      var items = []
      if (pc.subOrderList) {
        pc.subOrderList.forEach(function(so) {
          var subSpecialRequest = so.specialRequest || ''
          if (so.orderItemList) {
            so.orderItemList.forEach(function(item) {
              var itemNote = item.specialRequest || parseItemSpecialRequest(subSpecialRequest, item.uuid, item.name)
              items.push(Object.assign({}, item, { specialRequest: itemNote }))
            })
          }
        })
      }
      return {
        name: pc.guestName,
        isHost: pc.participantRole === 'HOST',
        orderId: pc.orderId,
        stage: pc.stage,
        locked: pc.locked,
        items: items,
        subTotal: pc.subTotal || 0,
      }
    })
    return {
      role: state.isHost ? 'host' : 'guest',
      groupOrderCode: state.inviteUuid,
      groupOrderName: state.group.groupName || (rootState.storeDetail && rootState.storeDetail.store && rootState.storeDetail.store.name ? rootState.storeDetail.store.name + ' Group Order' : 'Group Order'),
      myName: state.participantCart.guestName,
      limit: state.group.maxPreDiscountAmount,
      locked: state.participantCart.locked,
      lockedReason: state.participantCart.lockedReason,
      myStage: state.participantCart.stage,
      deadlineDate: state.group.countdownEndsAt,
      deadlineTime: state.group.countdownEndsAt,
      myItems: myItems,
      members: members,
    }
  },
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

var mutations = {
  SET_GROUP_STATE: function(state, payload) {
    state.group = payload.group
    // Every GET response (not just a failed mutation's immediate patch) needs the same
    // name-matched item.errors stamped on -- the backend's own item.errors is never
    // populated (see stampItemErrors), so without this a page refresh loses the per-item
    // pink highlight even though cart-level validationErrors/hasError survive fine.
    state.participantCart = stampItemErrors(payload.participantCart)
    state.participantCarts = (payload.participantCarts || []).map(stampItemErrors)
    state.isHost = payload.isHost
    state.joinAllowed = payload.joinAllowed
    state.checkoutEligible = payload.checkoutEligible !== undefined ? payload.checkoutEligible : false
    state.checkoutBlockedReasons = payload.checkoutBlockedReasons || []
    state.blockingParticipantCartOrderIds = payload.blockingParticipantCartOrderIds || []
    if (payload.group && payload.group.inviteUuid) {
      state.inviteUuid = payload.group.inviteUuid
    }
    if (payload.participantCart && payload.participantCart.orderId) {
      state.participantCartOrderId = payload.participantCart.orderId
    }
  },
  SET_INVITE_UUID: function(state, inviteUuid) {
    state.inviteUuid = inviteUuid
  },
  SET_PARTICIPANT_CART_ORDER_ID: function(state, id) {
    state.participantCartOrderId = id
  },
  SET_PARTICIPANT_CART: function(state, cart) {
    state.participantCart = cart
  },
  // Patches validationErrors onto the existing participantCart -- used when a mutation
  // itself fails (e.g. adding an out-of-stock item) so the same cartValidationErrors/
  // cartErrorMessage banner picks it up immediately, without waiting on a follow-up GET,
  // and without replacing the whole cart object (the failed mutation was never
  // persisted, so nothing else about the cart changed).
  //
  // Also stamps a matching .errors array onto the specific item(s) each error names, the
  // same field Cart.vue's doesItemHaveErrors/getItemErrors read for the per-item pink
  // highlight (see ddd.png) -- the normal cart gets this straight from the backend's
  // per-item errors field, but the mutation response has no item uuid/code to correlate
  // by (the backend's OrderValidationError.cause, the actual item reference, is
  // @JsonIgnore'd), only the human-readable message ("X is out of stock"). Matching each
  // item's own name as a prefix of that message is the only correlation available here --
  // good enough for this backend's consistent "{itemName} is out of stock" phrasing, but
  // a name-text match, not an id match, so treat it as best-effort highlighting, not a
  // source of truth (validationErrors above remains that).
  SET_PARTICIPANT_CART_VALIDATION_ERRORS: function(state, errors) {
    if (!state.participantCart) return
    errors = errors || []
    state.participantCart = stampItemErrors(Object.assign({}, state.participantCart, { validationErrors: errors }))
  },
  SET_GROUP_CHANGED_NOTIFICATION: function(state, notification) {
    state.groupChangedNotification = notification
  },
  SET_GROUP_VERSION: function(state, version) {
    if (state.group) state.group.version = version
  },
  SET_CART_VERSION: function(state, version) {
    if (state.participantCart) state.participantCart.version = version
  },
  SET_CART_LOCKED: function(state, payload) {
    if (state.participantCart) {
      var locked = typeof payload === 'object' ? payload.locked : payload
      var reason = typeof payload === 'object' ? payload.reason : null
      state.participantCart.locked = locked
      if (locked) {
        state.participantCart.lockedReason = reason || state.participantCart.lockedReason || 'COUNTDOWN'
      }
    }
  },
  SET_WS_CONNECTED: function(state, connected) {
    state.wsConnected = connected
  },
  SET_CHECKOUT_STATUS: function(state, status) {
    state.checkoutStatus = status
  },
  SET_LOADING: function(state, loading) {
    state.isLoading = loading
  },
  SET_ERROR: function(state, error) {
    state.error = error
  },
  SET_REMOVED: function(state, removed) {
    state.removed = removed
  },
  SET_LEFT: function(state, left) {
    state.left = left
  },
  SET_LEFT_GROUP_NAME: function(state, name) {
    state.leftGroupName = name
  },
  SET_RECOVERED_SESSION: function(state, payload) {
    state.recoveredSession = payload
  },
  SET_CANCELLED: function(state, cancelled) {
    state.cancelled = cancelled
  },
  RESET_GROUP_ORDER: function(state) {
    state.sessionGeneration += 1
    state.inviteUuid = null
    state.participantCartOrderId = null
    state.group = null
    state.participantCart = null
    state.participantCarts = []
    state.isHost = false
    state.joinAllowed = true
    state.checkoutEligible = false
    state.checkoutBlockedReasons = []
    state.blockingParticipantCartOrderIds = []
    state.wsConnected = false
    state.checkoutStatus = null
    state.isLoading = false
    state.error = null
    state.removed = false
    state.left = false
    state.leftGroupName = null
    state.cancelled = false
    state.recoveredSession = null
  },
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

var actions = {
  createGroup: async function({ commit, dispatch }, payload) {
    commit('RESET_GROUP_ORDER')
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      var apiPayload = {
        storeId: payload.storeId,
        groupName: payload.groupName,
        guestName: payload.guestName,
        countdownEndsAt: payload.countdownEndsAt || null,
        maxPreDiscountAmount: payload.maxPreDiscountAmount || null,
        orderType: payload.orderType || null,
        deliveryAddress: payload.deliveryAddress || null,
        scheduledTime: payload.scheduledTime || null,
      }
      var res = await this.$api.post('/group-orders', apiPayload)
      var responseData = res.data
      commit('SET_GROUP_STATE', responseData)
      applyGroupSettingsToCart(this, responseData)
      this.dispatch('fetchShoppingCart', { forceTimeSlots: true }).catch(function() {})
      persistSession(
        responseData.group.inviteUuid,
        responseData.participantCart.orderId,
        'HOST'
      )
      dispatch('connectWs', payload.wsHandlers || {})
      return responseData
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  getInvite: async function({ commit }, payload) {
    var inviteUuid = payload.inviteUuid
    commit('SET_ERROR', null)
    try {
      var res = await this.$api.get('/group-orders/invite/' + inviteUuid)
      return res.data
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    }
  },

  joinGroup: async function({ commit, dispatch }, payload) {
    var inviteUuid = payload.inviteUuid
    var guestName = payload.guestName
    var order = payload.order
    commit('RESET_GROUP_ORDER')
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      var res = await this.$api.post(
        '/group-orders/invite/' + inviteUuid + '/join',
        { guestName: guestName, order: order }
      )
      var responseData = res.data
      commit('SET_GROUP_STATE', responseData)
      applyGroupSettingsToCart(this, responseData)
      this.dispatch('fetchShoppingCart', { forceTimeSlots: true }).catch(function() {})
      persistSession(
        responseData.group.inviteUuid,
        responseData.participantCart.orderId,
        'GUEST'
      )
      dispatch('connectWs', payload.wsHandlers || {})
      return responseData
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // DEV-10823: multiple independent callers (Cart.vue, CouponCardDetail.vue, the
  // WS-disconnected poll fallback) each dispatch their own bare fetchGroupState() with
  // no way to know another is already in flight -- observed as a burst of duplicate
  // GET /group-orders/{inviteUuid} calls in the network trace, part of the same
  // concurrent-request pressure behind the GROUP_ORDER_VERSION_CONFLICT race. Collapse
  // concurrent calls targeting the same invite+cart into one shared request.
  fetchGroupState: function({ dispatch, state }, payload) {
    payload = payload || {}
    var inviteUuid = payload.inviteUuid || state.inviteUuid
    var participantCartOrderId = payload.participantCartOrderId || state.participantCartOrderId
    var key = state.sessionGeneration + '|' + (inviteUuid || '') + '|' + (participantCartOrderId || '')
    if (_inFlightFetchGroupState[key]) return _inFlightFetchGroupState[key]
    _inFlightFetchGroupState[key] = dispatch('_fetchGroupStateImpl', payload).finally(function() {
      delete _inFlightFetchGroupState[key]
    })
    return _inFlightFetchGroupState[key]
  },

  _fetchGroupStateImpl: async function({ commit, state, dispatch }, payload) {
    payload = payload || {}
    var inviteUuid = payload.inviteUuid || state.inviteUuid
    var participantCartOrderId = payload.participantCartOrderId || state.participantCartOrderId
    if (!inviteUuid) return
    var sessionGeneration = state.sessionGeneration

    commit('SET_ERROR', null)
    try {
      var params = participantCartOrderId ? { participantCartOrderId: participantCartOrderId } : {}
      var res = await this.$api.get('/group-orders/' + inviteUuid, { params: params })
      // The request may have been in-flight when the active session changed (e.g. a
      // background WS-disconnected poll for a PRIOR group, still pending when the user
      // joined a different one via GroupOrderJoinDialog). A stale response for an invite
      // that's no longer current must never overwrite the now-active group's state, and
      // must never be treated as "the current group got cancelled". Only reject when
      // state.inviteUuid is already set to something ELSE -- restoreSession/recoverGroupOrder
      // legitimately call this while state.inviteUuid is still null/unset. The
      // generation also rejects requests started before a merchant/cart reset.
      if (state.sessionGeneration !== sessionGeneration) return
      if (state.inviteUuid && state.inviteUuid !== inviteUuid) return
      var responseData = res.data
      if (!state.isHost && responseData && responseData.group && responseData.group.status === 'CANCELLED') {
        dispatch('handleCancelled')
        return responseData
      }
      // Notify host when a new participant joins
      if (state.isHost && state.participantCarts && responseData.participantCarts) {
        var oldIds = state.participantCarts.map(function(pc) { return pc.orderId })
        var newCarts = responseData.participantCarts.filter(function(pc) {
          return pc.stage !== 'REMOVED' && pc.stage !== 'COMPLETED' && pc.participantRole !== 'HOST' && oldIds.indexOf(pc.orderId) === -1
        })
        newCarts.forEach(function(pc) {
          dispatch('showSnack', { text: (pc.guestName || 'Someone') + ' joined the group order.', color: 'primary', timeout: 4000 }, { root: true })
        })
      }

      // Detect host-driven changes and notify guests (debounced to batch rapid sequential changes)
      if (!state.isHost && state.group && responseData.group) {
        var oldOrderType = state.group.orderType
        var newOrderType = responseData.group.orderType
        // Treat "now" and null/undefined as equivalent — no specific scheduled time
        var normalizeTime = function(t) { return (t && t !== 'now') ? t : null }
        var oldScheduledTime = normalizeTime(state.group.scheduledTime)
        var newScheduledTime = normalizeTime(responseData.group.scheduledTime)
        var oldDeliveryAddress = state.group.deliveryAddress
        var newDeliveryAddress = responseData.group.deliveryAddress

        var orderTypeChanged = oldOrderType !== newOrderType
        var scheduledTimeChanged = oldScheduledTime !== newScheduledTime
        var deliveryAddressChanged = JSON.stringify(oldDeliveryAddress) !== JSON.stringify(newDeliveryAddress)


        if (orderTypeChanged || scheduledTimeChanged || deliveryAddressChanged) {
          // Merge into any pending notification so rapid back-to-back changes show as one dialog
          var pending = state.groupChangedNotification
          var baseOldOrderType = pending ? pending.oldOrderType : oldOrderType
          var baseOldScheduledTime = pending ? pending.oldScheduledTime : oldScheduledTime
          var baseOldDeliveryAddress = pending ? pending.oldDeliveryAddress : oldDeliveryAddress

          if (_notificationTimer) clearTimeout(_notificationTimer)
          // Build merged notification immediately into state (watcher in App.vue will debounce the dialog)
          commit('SET_GROUP_CHANGED_NOTIFICATION', {
            oldOrderType: baseOldOrderType,
            newOrderType: newOrderType,
            oldScheduledTime: baseOldScheduledTime,
            newScheduledTime: newScheduledTime,
            oldDeliveryAddress: baseOldDeliveryAddress,
            newDeliveryAddress: newDeliveryAddress,
            pending: true,
          })
          // Wait 1.5s for more changes, then mark as ready to show
          _notificationTimer = setTimeout(function() {
            _notificationTimer = null
            commit('SET_GROUP_CHANGED_NOTIFICATION', Object.assign({}, state.groupChangedNotification, { pending: false }))
          }, 1500)
        }
      }

      var prevGroup = state.group
      commit('SET_GROUP_STATE', responseData)
      // If the group has moved to checkout and we're a participant, ensure our cart is locked
      // regardless of what the server returned (backend may not have updated locked yet)
      if (!state.isHost && responseData.group && responseData.group.status === 'CHECKOUT') {
        commit('SET_CART_LOCKED', { locked: true, reason: 'CHECKOUT' })
      }
      // Re-sync cart settings only when group-level fields changed, not on every poll
      var newGroup = responseData.group
      if (!prevGroup
        || prevGroup.orderType !== newGroup.orderType
        || prevGroup.scheduledTime !== newGroup.scheduledTime
        || JSON.stringify(prevGroup.deliveryAddress) !== JSON.stringify(newGroup.deliveryAddress)) {
        applyGroupSettingsToCart(this, responseData)
      }
      var coupon = responseData.participantCart &&
        responseData.participantCart.subOrderList &&
        responseData.participantCart.subOrderList[0] &&
        responseData.participantCart.subOrderList[0].coupons &&
        responseData.participantCart.subOrderList[0].coupons[0]
      commit('SET_ACTIVE_COUPON', coupon || null, { root: true })
      return responseData
    } catch (error) {
      // Same staleness guard as the success path -- a failure for an invite that's no
      // longer the active one (e.g. a background poll for a group the user already left
      // by joining a different one) must not affect the now-active session at all.
      if (state.sessionGeneration !== sessionGeneration) return
      if (state.inviteUuid && state.inviteUuid !== inviteUuid) return
      var httpStatus = error && error.response && error.response.status
      // If the group is gone (404/410) and this is a non-host participant, treat as cancelled
      if ((httpStatus === 404 || httpStatus === 410) && !state.isHost && state.inviteUuid) {
        dispatch('handleCancelled')
        return
      }
      commit('SET_ERROR', error)
      throw error
    }
  },

  restoreSession: async function({ dispatch, commit, state }, wsHandlers) {
    // Fire-and-forget: retry any leave that failed previously (e.g. network was down)
    dispatch('retryPendingLeave')

    if (state.inviteUuid && state.group && state.participantCart) return true

    var inviteUuid = lsGet(LS_KEYS.inviteUuid)
    var participantCartOrderId = lsGet(LS_KEYS.participantCartOrderId)
    if (!inviteUuid || !participantCartOrderId) return false

    var sessionGeneration = state.sessionGeneration
    try {
      await dispatch('fetchGroupState', { inviteUuid: inviteUuid, participantCartOrderId: participantCartOrderId })
      if (state.sessionGeneration !== sessionGeneration) return false
      var groupStatus = this.state.groupOrder && this.state.groupOrder.group && this.state.groupOrder.group.status
      if (groupStatus === 'CANCELLED' || groupStatus === 'PAID') {
        dispatch('clearGroupOrder')
        return false
      }
      applyGroupSettingsToCart(this, this.state.groupOrder)
      this.dispatch('fetchShoppingCart', { forceTimeSlots: true }).catch(function() {})
      dispatch('connectWs', wsHandlers || {})
      return true
    } catch (e) {
      if (state.sessionGeneration !== sessionGeneration) return false
      dispatch('clearGroupOrder')
      return false
    }
  },

  reapplyGroupSettings: function({ state }) {
    applyGroupSettingsToCart(this, state)
  },

  clearGroupOrder: function({ commit, dispatch }) {
    dispatch('disconnectWs')
    clearSession()
    commit('RESET_GROUP_ORDER')
    commit('SET_LOCAL_CART', { cart: [] }, { root: true })
  },

  mutateCart: async function({ commit, state }, payload) {
    payload = payload || {}
    var operations = payload.operations
    var specialRequest = payload.specialRequest
    var inviteUuid = state.inviteUuid
    var cartOrderId = state.participantCartOrderId
    var expectedCartVersion = state.participantCart ? state.participantCart.version : null

    var actions = (operations || []).map(function(op) {
      var action = Object.assign({}, op)
      if (op.type === 'ADD') action.type = 'ITEM_ADD'
      else if (op.type === 'REMOVE') action.type = 'ITEM_REMOVE'
      else if (op.type === 'EDIT') action.type = 'ITEM_EDIT'
      return action
    })
    if (specialRequest !== undefined) {
      actions.push({ type: 'SET_SPECIAL_REQUEST', specialRequest: specialRequest })
    }

    commit('SET_ERROR', null)
    try {
      var res = await this.$api.post(
        '/group-orders/' + inviteUuid + '/participant-carts/' + cartOrderId + '/mutations',
        { expectedCartVersion: expectedCartVersion, actions: actions }
      )
      var responseData = res.data
      if (responseData.cart) {
        commit('SET_PARTICIPANT_CART', responseData.cart)
      }
      return responseData
    } catch (error) {
      var errorCode = error.response && error.response.data && error.response.data.errorCode
      if (error.response && error.response.status === 409) {
        if (errorCode === 'GROUP_ORDER_CART_LIMIT_EXCEEDED') {
          this.dispatch('showSnack', {
            text: 'This item would exceed the spending limit set by the host.',
            color: 'error'
          }, { root: true })
        } else {
          await this.dispatch('groupOrder/fetchGroupState')
        }
      } else if (errorCode === 'ORD_ERR_INVALID_CART') {
        // Same nested errors[] shape as the normal cart's validationErrors (errorCode +
        // message per item) -- feed it into the same field so cartValidationErrors/
        // cartErrorMessage surface it immediately, same as an out-of-stock item does on
        // the normal (non-group) cart, instead of only console.error'ing it away.
        var nestedErrors = (error.response && error.response.data && error.response.data.errors) || []
        commit('SET_PARTICIPANT_CART_VALIDATION_ERRORS', nestedErrors)
        // The mutation error response has no per-item correlation (which item failed
        // isn't identifiable from errors[] alone -- the backend's OrderValidationError.cause
        // is @JsonIgnore'd) and never returns the cart on failure, so there's no way to
        // highlight the SPECIFIC item here. A GET does: it re-validates and returns the
        // full subOrderList with each item's own .errors populated, the field Cart.vue's
        // doesItemHaveErrors/getItemErrors actually read for the per-item pink highlight.
        await this.dispatch('groupOrder/fetchGroupState').catch(function() {})
      }
      commit('SET_ERROR', error)
      throw error
    }
  },

  updateCart: async function({ commit, state }, payload) {
    var cart = payload.cart
    var isRetry = payload._isRetry || false
    var inviteUuid = state.inviteUuid
    var cartOrderId = state.participantCartOrderId
    var expectedCartVersion = state.participantCart ? state.participantCart.version : null

    commit('SET_ERROR', null)
    try {
      var res = await this.$api.put(
        '/group-orders/' + inviteUuid + '/participant-carts/' + cartOrderId,
        { expectedCartVersion: expectedCartVersion, cart: cart }
      )
      commit('SET_GROUP_STATE', res.data)
      return res.data
    } catch (error) {
      // See updateGroup's matching comment -- concurrent validateScheduledTime/cart-refresh
      // traffic commonly makes this a stale-read race rather than a real conflict. Re-sync
      // and retry ONCE with the fresh version before surfacing an error.
      if (error.response && error.response.status === 409 && !isRetry) {
        await this.dispatch('groupOrder/fetchGroupState')
        return this.dispatch('groupOrder/updateCart', Object.assign({}, payload, { _isRetry: true }))
      }
      if (error.response && error.response.status === 409 && isRetry) {
        error.isGroupVersionConflict = true
      }
      commit('SET_ERROR', error)
      throw error
    }
  },

  readyCart: async function({ commit, state, dispatch }) {
    var inviteUuid = state.inviteUuid
    var cartOrderId = state.participantCartOrderId

    commit('SET_ERROR', null)
    try {
      var res = await this.$api.post(
        '/group-orders/' + inviteUuid + '/participant-carts/' + cartOrderId + '/ready'
      )
      commit('SET_GROUP_STATE', res.data)
      dispatch('fetchGroupState')
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    }
  },

  lockAllCarts: async function({ commit, state }) {
    var inviteUuid = state.inviteUuid
    var hostParticipantCartOrderId = state.participantCartOrderId

    commit('SET_ERROR', null)
    try {
      var res = await this.$api.post(
        '/group-orders/' + inviteUuid + '/participant-carts/lock-all',
        { hostParticipantCartOrderId: hostParticipantCartOrderId }
      )
      commit('SET_GROUP_STATE', res.data)
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    }
  },

  unlockAllCarts: async function({ commit, state, dispatch }) {
    var inviteUuid = state.inviteUuid
    var hostParticipantCartOrderId = state.participantCartOrderId

    commit('SET_ERROR', null)
    try {
      var res = await this.$api.post(
        '/group-orders/' + inviteUuid + '/participant-carts/unlock-all',
        { hostParticipantCartOrderId: hostParticipantCartOrderId }
      )
      commit('SET_GROUP_STATE', res.data)
      dispatch('fetchGroupState')
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    }
  },

  unlockCart: async function({ commit, state, dispatch }, payload) {
    var targetCartOrderId = payload.targetCartOrderId
    var inviteUuid = state.inviteUuid
    var hostParticipantCartOrderId = payload.hostParticipantCartOrderId !== undefined ? payload.hostParticipantCartOrderId : state.participantCartOrderId

    commit('SET_ERROR', null)
    try {
      var res = await this.$api.post(
        '/group-orders/' + inviteUuid + '/participant-carts/' + targetCartOrderId + '/unlock',
        { hostParticipantCartOrderId: hostParticipantCartOrderId }
      )
      commit('SET_GROUP_STATE', res.data)
      dispatch('fetchGroupState')
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    }
  },

  removeParticipant: async function({ commit, state, dispatch }, payload) {
    var targetCartOrderId = payload.targetCartOrderId
    var inviteUuid = state.inviteUuid

    commit('SET_ERROR', null)
    try {
      var params = state.participantCartOrderId ? { participantCartOrderId: state.participantCartOrderId } : {}
      var res = await this.$api.delete(
        '/group-orders/' + inviteUuid + '/participant-carts/' + targetCartOrderId,
        { params: params }
      )
      commit('SET_GROUP_STATE', res.data)
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    }
  },

  // Bulk removes unready participant carts before host checkout (DEV-10330). Never includes the
  // host's own cart -- the backend rejects that with GROUP_ORDER_HOST_CART_CANNOT_BE_REMOVED, and
  // emptying the host's own cart doesn't fit the flow anyway. The backend call is best-effort (may
  // partially fail under concurrent edits), so retry only the carts that failed, up to 3 attempts.
  // Throws if any cart is still unremoved after retries so the caller does NOT proceed to checkout
  // with unready carts silently left behind.
  removeParticipants: async function({ commit, state }, payload) {
    var inviteUuid = state.inviteUuid
    var hostParticipantCartOrderId = state.participantCartOrderId
    var remaining = (payload.participantOrderIds || []).filter(function(id) {
      return id && id !== hostParticipantCartOrderId
    })
    if (!remaining.length) return null

    commit('SET_ERROR', null)
    // One "X left the group" snack per removed cart would otherwise stomp the checkout
    // error/success snack shown right after -- the snackbar only holds one message.
    if (_wsInstance) _wsInstance.suppressLeftNotifications = true
    try {
      var lastResponse = null
      for (var attempt = 0; attempt < 3 && remaining.length; attempt++) {
        try {
          var res = await this.$api.post(
            '/group-orders/' + inviteUuid + '/participant-carts/remove',
            { participantOrderIds: remaining, hostParticipantCartOrderId: hostParticipantCartOrderId }
          )
          lastResponse = res.data
          commit('SET_GROUP_STATE', res.data.groupState)
          if (res.data.allRemoved) return lastResponse
          remaining = (res.data.failed || []).map(function(f) { return f.participantOrderId })
        } catch (error) {
          commit('SET_ERROR', error)
          throw error
        }
      }
      if (remaining.length) {
        var err = new Error('Failed to remove all unready participant carts after retries')
        err.unremovedParticipantOrderIds = remaining
        commit('SET_ERROR', err)
        throw err
      }
      return lastResponse
    } finally {
      if (_wsInstance) _wsInstance.suppressLeftNotifications = false
    }
  },

  updateLimit: async function({ dispatch }, payload) {
    return dispatch('updateGroup', {
      actions: [{ type: 'SET_LIMIT', maxPreDiscountAmount: payload.maxPreDiscountAmount }],
    })
  },

  updateScheduledTime: async function({ dispatch }, payload) {
    return dispatch('updateGroup', {
      actions: [{ type: 'SET_SCHEDULED_TIME', scheduledTime: payload.scheduledTime }],
    })
  },

  updateDeadline: async function({ dispatch }, payload) {
    return dispatch('updateGroup', {
      actions: [{ type: 'SET_COUNTDOWN_ENDS_AT', countdownEndsAt: payload.countdownEndsAt || null }],
    })
  },

  updateOrderType: async function({ dispatch }, payload) {
    // Backend applies SET_ORDER_TYPE, SET_DELIVERY_ADDRESS and SET_SCHEDULED_TIME as
    // independent actions -- SET_ORDER_TYPE alone does NOT persist deliveryAddress, so
    // DELIVERY switches must always include it in the same batch or the group is left with
    // orderType=DELIVERY and no address. scheduledTime is optional (only PickupDeliveryEditDialog
    // / DeliveryDialog's "confirm switch" flows set it here) so a plain order-type change
    // elsewhere doesn't clobber an unrelated, already-set schedule.
    var actions = [{ type: 'SET_ORDER_TYPE', orderType: payload.orderType }]
    if (payload.orderType === 'DELIVERY') {
      actions.push({ type: 'SET_DELIVERY_ADDRESS', deliveryAddress: payload.deliveryAddress || null })
    }
    if (payload.scheduledTime !== undefined) {
      actions.push({ type: 'SET_SCHEDULED_TIME', scheduledTime: payload.scheduledTime })
    }
    return dispatch('updateGroup', { actions: actions })
  },

  updateGroup: async function({ commit, state, dispatch }, payload) {
    var groupActions = payload.actions
    var isRetry = payload._isRetry || false
    var inviteUuid = state.inviteUuid
    var expectedGroupVersion = state.group ? state.group.version : null
    var hostParticipantCartOrderId = state.participantCartOrderId

    commit('SET_ERROR', null)
    try {
      var res = await this.$api.post(
        '/group-orders/' + inviteUuid + '/mutations',
        {
          expectedGroupVersion: expectedGroupVersion,
          hostParticipantCartOrderId: hostParticipantCartOrderId,
          actions: groupActions,
        }
      )
      commit('SET_GROUP_STATE', res.data)
      // Every settings change (order type, delivery address, scheduled time, ...) needs to
      // reflect back onto the regular cart store the UI actually reads from -- same as
      // createGroup/joinGroup/restoreSession already do. Without this, a successful mutation
      // updates state.groupOrder.group but leaves state.cart (getOrderType, getDeliveryAddress,
      // getCheckoutInfo) stale, so the UI can appear to not have changed at all.
      applyGroupSettingsToCart(this, res.data)
      return res.data
    } catch (error) {
      // 409 GROUP_ORDER_VERSION_CONFLICT: the concurrent validateScheduledTime/cart-refresh
      // traffic this action itself triggers (see group-order-switch.service.js) commonly
      // bumps the group version between when this action reads it and when the request lands
      // -- a stale-read race, not a real conflict. Re-sync and retry ONCE with the fresh
      // version before surfacing an error; only give up if the retry ALSO 409s -- at that
      // point something kept the version moving even against a just-fetched value (most
      // likely a genuine concurrent edit, though not certain enough to name a specific cause
      // to the user).
      if (error.response && error.response.status === 409 && !isRetry) {
        await dispatch('fetchGroupState')
        return dispatch('updateGroup', Object.assign({}, payload, { _isRetry: true }))
      }
      // Flag a conflict that survived the retry above -- callers use this to show a
      // "couldn't update right now" message distinct from the generic failure copy,
      // without asserting a specific cause we can't fully confirm.
      if (error.response && error.response.status === 409 && isRetry) {
        error.isGroupVersionConflict = true
      }
      commit('SET_ERROR', error)
      throw error
    }
  },

  // Sends the checkout page's order notes textarea value, if changed. Call once right before
  // checkout -- not on every keystroke.
  updateOrderNotes: async function({ dispatch, state }, payload) {
    var orderNotes = (payload && payload.orderNotes) || null
    var current = state.group ? (state.group.orderNotes || null) : null
    if (orderNotes === current) return
    return dispatch('updateGroup', {
      actions: [{ type: 'SET_ORDER_NOTES', orderNotes: orderNotes }],
    })
  },

  updateIncludeCutlery: async function({ dispatch, state }, payload) {
    var includeCutlery = !!(payload && payload.includeCutlery)
    var current = !!(state.group && state.group.includeCutlery)
    if (includeCutlery === current) return
    return dispatch('updateGroup', {
      actions: [{ type: 'SET_INCLUDE_CUTLERY', includeCutlery: includeCutlery }],
    })
  },

  // Sends the checkout page's delivery instructions (free-text notes + canned "leave at
  // door" style message, same as PickupDetailsCard.vue / cart.js payOrder combine them), if
  // changed. Call once right before checkout -- not on every keystroke.
  updateDeliveryInstructions: async function({ dispatch, state }, payload) {
    var deliveryInstructions = (payload && payload.deliveryInstructions) || null
    var current = state.group ? (state.group.deliveryInstructions || null) : null
    if (deliveryInstructions === current) return
    return dispatch('updateGroup', {
      actions: [{ type: 'SET_DELIVERY_INSTRUCTIONS', deliveryInstructions: deliveryInstructions }],
    })
  },


  setCoupon: async function({ commit, state }, payload) {
    var couponCode = payload.couponCode
    var inviteUuid = state.inviteUuid
    var hostCartOrderId = state.participantCartOrderId
    var expectedCartVersion = state.participantCart ? state.participantCart.version : null

    commit('SET_ERROR', null)
    try {
      var res = await this.$api.put(
        '/group-orders/' + inviteUuid + '/participant-carts/' + hostCartOrderId + '/coupon',
        { expectedCartVersion: expectedCartVersion, couponCode: couponCode }
      )
      commit('SET_GROUP_STATE', res.data)
      var coupon = res.data.participantCart &&
        res.data.participantCart.subOrderList &&
        res.data.participantCart.subOrderList[0] &&
        res.data.participantCart.subOrderList[0].coupons &&
        res.data.participantCart.subOrderList[0].coupons[0]
      commit('SET_ACTIVE_COUPON', coupon || null, { root: true })
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    }
  },

  removeCoupon: async function({ commit, state }) {
    var inviteUuid = state.inviteUuid
    var hostCartOrderId = state.participantCartOrderId
    // The backend route is DELETE .../coupon/{couponCode}. Our backend expects the code in
    // the path (not request body), so use the currently-applied coupon code from state,
    // same place setCoupon's response reads it back from.
    var currentCoupon = state.participantCart &&
      state.participantCart.subOrderList &&
      state.participantCart.subOrderList[0] &&
      state.participantCart.subOrderList[0].coupons &&
      state.participantCart.subOrderList[0].coupons[0]
    var couponCode = currentCoupon && currentCoupon.code

    if (!couponCode) return

    commit('SET_ERROR', null)
    try {
      var res = await this.$api.delete(
        '/group-orders/' + inviteUuid + '/participant-carts/' + hostCartOrderId + '/coupon/' + encodeURIComponent(couponCode)
      )
      commit('SET_GROUP_STATE', res.data)
      // Every caller needs this cleared -- CouponDetails.vue's onRemoveCoupon didn't
      // commit it itself, leaving the checkout screen showing a stale applied coupon
      // until the next unrelated refresh. Commit it here once instead of relying on
      // each caller to remember.
      commit('SET_ACTIVE_COUPON', null, { root: true })
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    }
  },

  cancelGroup: async function({ commit, dispatch, state }) {
    var inviteUuid = state.inviteUuid
    var hostParticipantCartOrderId = state.participantCartOrderId

    commit('SET_ERROR', null)
    try {
      await this.$api.delete(
        '/group-orders/' + inviteUuid,
        { params: { hostParticipantCartOrderId: hostParticipantCartOrderId } }
      )
    } catch (e) { /* best-effort — clear locally regardless */ }
    dispatch('clearGroupOrder')
  },

  checkout: async function({ commit, state, dispatch }, payload) {
    payload = payload || {}
    var paymentProvider = payload.paymentProvider
    var paymentRequest = payload.paymentRequest
    var expectedTotal = payload.expectedTotal
    var reuseIdempotencyKey = payload.reuseIdempotencyKey || false

    var inviteUuid = state.inviteUuid
    var hostParticipantCartOrderId = state.participantCartOrderId
    var expectedGroupVersion = state.group ? state.group.version : null

    var idempotencyKey = reuseIdempotencyKey ? lsGet(LS_KEYS.idempotencyKey) : null
    if (!idempotencyKey) {
      idempotencyKey = require('uuid').v4()
      lsSet(LS_KEYS.idempotencyKey, idempotencyKey)
    }

    commit('SET_CHECKOUT_STATUS', 'processing')
    commit('SET_ERROR', null)
    var storeDetail = this.state.storeDetail && this.state.storeDetail.store
    var returnUrl = storeDetail
      ? `${window.location.origin}/owa/snappy/detail/${storeDetail.groupId}/${storeDetail.storeId}/3ds_success_handler`
      : `${window.location.origin}/3ds_success_handler`

    try {
      var res = await this.$api.post('/group-orders/' + inviteUuid + '/checkout', {
        expectedGroupVersion: expectedGroupVersion,
        hostParticipantCartOrderId: hostParticipantCartOrderId,
        idempotencyKey: idempotencyKey,
        expectedTotal: expectedTotal,
        paymentProvider: paymentProvider,
        paymentRequest: paymentRequest,
        returnUrl: returnUrl,
      })
      var responseData = res.data
      var paymentResponse = responseData.paymentResponse || responseData


      // Handle Stripe 3DS — user completes auth in Stripe modal, then we confirm with backend
      if (paymentResponse.intentSecret) {
        const stripe = await loadStripe(
          process.env.VUE_APP_STRIPE,
          !paymentResponse.destination ? { stripeAccount: paymentResponse.stripeAccountId } : null
        )
        const result = await stripe.confirmCardPayment(paymentResponse.intentSecret)
        const stripeStatus = result && result.paymentIntent && result.paymentIntent.status
        if (responseData.gatewayPaymentId) lsSet(LS_KEYS.gatewayPaymentId, responseData.gatewayPaymentId)
        if (stripeStatus === 'succeeded' || stripeStatus === 'requires_capture') {
          // Stripe auth done — ask backend to reconcile
          var confirmData = await dispatch('checkoutConfirm')
          return Object.assign({}, responseData, confirmData || {})
        } else {
          // 3DS auth failed, cancelled, or charge declined — backend is in WAITING_PAYMENT,
          // call confirm so it can reconcile and reopen the group for retry.
          const errMsg = (result && result.error && result.error.message) || 'Payment authentication failed. Please try again.'
          try {
            await dispatch('checkoutConfirm')
          } catch (confirmErr) {
            // handled by checkoutConfirm; surface the Stripe message below
          }
          commit('SET_CHECKOUT_STATUS', 'failed')
          const err = new Error(errMsg)
          err.is3dsFailure = true
          throw err
        }
      }

      // Handle Global Payments 3DS iframe — mirror cart.js
      if (paymentResponse.paymentType === 'GLOBAL_PAYMENTS' && paymentResponse.redirectUrl) {
        this.commit('SET_STRIPE_INFO', { paymentReq: payload, paymentResp: paymentResponse })
        this.commit('SET_IFRAME', paymentResponse.redirectUrl)
        // Resolve — App.vue message listener handles the rest after iframe completes
        return responseData
      }

      if (responseData.groupStatus === 'PAID' || responseData.groupStatus === 'PAY_REQUEST') {
        commit('SET_CHECKOUT_STATUS', 'paid')
        lsRemove(LS_KEYS.idempotencyKey)
        clearSession()
      } else if (responseData.groupStatus === 'WAITING_PAYMENT') {
        commit('SET_CHECKOUT_STATUS', 'waiting_payment')
      } else {
        commit('SET_CHECKOUT_STATUS', responseData.retryAllowed ? 'failed' : null)
      }
      return responseData
    } catch (error) {
      // Let confirm/3DS errors pass through — they already set status and error
      if (error.isConfirmFailure || error.is3dsFailure) throw error
      commit('SET_CHECKOUT_STATUS', 'failed')
      commit('SET_ERROR', error)
      throw error
    }
  },

  // Call after the frontend completes a gateway action (Stripe 3DS, redirect) that resulted in
  // WAITING_PAYMENT. Asks the backend to reconcile the active payment attempt and updates
  // checkoutStatus based on reconciliationStatus.
  checkoutConfirm: async function({ commit, state, dispatch }, options) {
    options = options || {}
    var retryCount = options._retryCount || 0
    var MAX_RETRIES = 3
    var RETRY_DELAY_MS = 2000

    var inviteUuid = state.inviteUuid
    var hostParticipantCartOrderId = state.participantCartOrderId
    var idempotencyKey = lsGet(LS_KEYS.idempotencyKey)
    var gatewayPaymentId = lsGet(LS_KEYS.gatewayPaymentId)

    if (!inviteUuid || !hostParticipantCartOrderId || !idempotencyKey) {
      commit('SET_CHECKOUT_STATUS', 'failed')
      var missingErr = new Error('Missing group order session data for checkout confirm.')
      commit('SET_ERROR', missingErr)
      throw missingErr
    }

    try {
      var res = await this.$api.post(
        '/group-orders/' + inviteUuid + '/checkout/confirm',
        {
          hostParticipantCartOrderId: hostParticipantCartOrderId,
          idempotencyKey: idempotencyKey,
          gatewayPaymentId: gatewayPaymentId || undefined,
        }
      )
      var data = res.data
      var reconciliationStatus = data.reconciliationStatus

      if (data.groupStatus === 'PAID') {
        // Authoritative: the group is paid regardless of what reconciliationStatus says
        // (e.g. SKIPPED can mean "already fully settled, nothing left to reconcile", not
        // "payment never happened" -- checking groupStatus first avoids misreading that
        // as a failure/no-op and silently dropping the success dialog + navigation).
        commit('SET_CHECKOUT_STATUS', 'paid')
        lsRemove(LS_KEYS.idempotencyKey)
        lsRemove(LS_KEYS.gatewayPaymentId)
        clearSession()
      } else if (reconciliationStatus === 'PAID') {
        commit('SET_CHECKOUT_STATUS', 'paid')
        lsRemove(LS_KEYS.idempotencyKey)
        lsRemove(LS_KEYS.gatewayPaymentId)
        clearSession()
      } else if (reconciliationStatus === 'STILL_WAITING') {
        if (retryCount < MAX_RETRIES) {
          // Backend hasn't reconciled yet — wait briefly and retry
          await new Promise(function(resolve) { setTimeout(resolve, RETRY_DELAY_MS) })
          return dispatch('checkoutConfirm', { _retryCount: retryCount + 1 })
        }
        // Exhausted retries — keep waiting_payment so WS can deliver the final state
        commit('SET_CHECKOUT_STATUS', 'waiting_payment')
      } else if (reconciliationStatus === 'FAILED_REOPENED') {
        // Payment failed; backend reopened the group so host can retry with a fresh key
        lsRemove(LS_KEYS.idempotencyKey)
        lsRemove(LS_KEYS.gatewayPaymentId)
        commit('SET_CHECKOUT_STATUS', 'failed')
        var failErr = new Error(data.reason || 'Payment failed. Please try again.')
        failErr.isConfirmFailure = true
        failErr.reconciliationStatus = reconciliationStatus
        commit('SET_ERROR', failErr)
        throw failErr
      } else if (reconciliationStatus === 'SKIPPED') {
        // No active payment attempt and group isn't PAID either — re-fetch group state
        // so UI is consistent
        lsRemove(LS_KEYS.gatewayPaymentId)
        try { await dispatch('fetchGroupState') } catch (e) { /* best-effort */ }
        commit('SET_CHECKOUT_STATUS', null)
      } else {
        // Unknown status — treat as still waiting so WS can recover
        commit('SET_CHECKOUT_STATUS', 'waiting_payment')
      }

      return data
    } catch (error) {
      // Re-throw confirm-specific errors unchanged; wrap API errors
      if (error.isConfirmFailure) throw error
      commit('SET_CHECKOUT_STATUS', 'failed')
      commit('SET_ERROR', error)
      throw error
    }
  },

  connectWs: function({ commit, state, dispatch }, handlers) {
    handlers = handlers || {}
    if (!state.inviteUuid || !state.participantCartOrderId) return

    if (_wsInstance) {
      _wsInstance.disconnect()
      _wsInstance = null
    }

    _wsInstance = new GroupOrderWebSocketService({
      inviteUuid: state.inviteUuid,
      participantCartOrderId: state.participantCartOrderId,
      store: this,
      router: this.$router,
      handlers: {
        onRemoved: handlers.onRemoved || null,
        onGroupPaid: handlers.onGroupPaid || null,
        onCountdownLocked: handlers.onCountdownLocked || null,
        onError: handlers.onError || null,
      },
    })
    _wsInstance.connect()
  },

  // Bind the logged-in customer's JWT identity to their participant cart.
  // Called after login if the cart has no customerId yet.
  bindCustomerId: async function({ state }) {
    var inviteUuid = state.inviteUuid
    var participantCartOrderId = state.participantCartOrderId
    var participantCart = state.participantCart
    if (!inviteUuid || !participantCartOrderId) return
    if (participantCart && participantCart.customerId) return // already bound
    try {
      await this.$api.post(
        '/group-orders/' + inviteUuid + '/participant-carts/' + participantCartOrderId + '/mutations',
        {
          expectedCartVersion: participantCart ? participantCart.version : null,
          actions: [{ type: 'SET_CUSTOMER_ID' }],
        }
      )
    } catch (e) {
      // 409 ALREADY_BOUND to same customer = OK, ignore silently
      var errorCode = e && e.response && e.response.data && e.response.data.errorCode
      if (errorCode !== 'GROUP_ORDER_CART_ALREADY_BOUND') {
        console.warn('[GroupOrder] bindCustomerId failed:', errorCode)
      }
    }
  },

  // After login, check if the customer has any active group orders on the server.
  // Used for cross-device recovery.
  recoverGroupOrder: async function({ commit, dispatch, state }, wsHandlers) {
    // Already in an active session — no need to recover
    if (state.inviteUuid && state.group && state.participantCart) {
      dispatch('bindCustomerId')
      return
    }
    try {
      var res = await this.$api.get('/customer/order/v2')
      var groupOrders = res.data && res.data.groupOrders
      if (!groupOrders || !groupOrders.length) return

      // Take the most recent active group order (sorted by groupVersion desc by backend)
      var summary = groupOrders[0]
      if (!summary || !summary.inviteUuid || !summary.participantCartOrderId) return

      // Restore session via fetchGroupState then connect WS
      await dispatch('fetchGroupState', {
        inviteUuid: summary.inviteUuid,
        participantCartOrderId: summary.participantCartOrderId,
      })

      var groupStatus = this.state.groupOrder && this.state.groupOrder.group && this.state.groupOrder.group.status
      if (groupStatus === 'CANCELLED' || groupStatus === 'PAID') {
        dispatch('clearGroupOrder')
        return
      }

      persistSession(summary.inviteUuid, summary.participantCartOrderId, summary.isHost ? 'HOST' : 'GUEST')
      applyGroupSettingsToCart(this, this.state.groupOrder)
      dispatch('connectWs', wsHandlers || {})
      dispatch('bindCustomerId')

      var groupName = this.state.groupOrder.group ? this.state.groupOrder.group.groupName : 'Group Order'
      var isHost = this.state.groupOrder.isHost
      commit('SET_RECOVERED_SESSION', { groupName: groupName, isHost: isHost })
    } catch (e) {
      // No group orders or endpoint not available — fail silently, never show error
      commit('SET_ERROR', null)
    }
  },

  disconnectWs: function() {
    if (_wsInstance) {
      _wsInstance.disconnect()
      _wsInstance = null
    }
  },

  leaveGroup: async function({ commit, dispatch, state }, payload) {
    var silent = payload && payload.silent
    var inviteUuid = state.inviteUuid
    var participantCartOrderId = state.participantCartOrderId
    var wasInGroup = !!(inviteUuid && participantCartOrderId)
    // Disconnect WS first so the PARTICIPANT_REMOVED event doesn't fire back to us
    dispatch('disconnectWs')
    var groupName = state.group ? state.group.groupName : null
    // Clear locally immediately — user is out regardless of backend response
    clearSession()
    commit('RESET_GROUP_ORDER')
    // Only show the "you have left" dialog if the user was actually in a group order and not silently leaving
    if (wasInGroup && !silent) {
      commit('SET_LEFT', true)
      commit('SET_LEFT_GROUP_NAME', groupName)
    }
    // Best-effort DELETE — token is valid at this point in both silent and normal flows
    if (inviteUuid && participantCartOrderId) {
      try {
        await this.$api.delete(
          '/group-orders/' + inviteUuid + '/participant-carts/' + participantCartOrderId,
          { params: { participantCartOrderId: participantCartOrderId } }
        )
        lsRemove(LS_KEYS.pendingLeave)
      } catch (e) {
        lsSet(LS_KEYS.pendingLeave, JSON.stringify({ inviteUuid, participantCartOrderId }))
      }
    }
  },

  retryPendingLeave: async function() {
    var raw = lsGet(LS_KEYS.pendingLeave)
    if (!raw) return
    var pending
    try { pending = JSON.parse(raw) } catch (e) { lsRemove(LS_KEYS.pendingLeave); return }
    if (!pending || !pending.inviteUuid || !pending.participantCartOrderId) {
      lsRemove(LS_KEYS.pendingLeave)
      return
    }
    try {
      await this.$api.delete(
        '/group-orders/' + pending.inviteUuid + '/participant-carts/' + pending.participantCartOrderId,
        { params: { participantCartOrderId: pending.participantCartOrderId } }
      )
      lsRemove(LS_KEYS.pendingLeave)
    } catch (e) {
      var status = e && e.response && e.response.status
      // 401/403/404/410 = auth gone or group no longer exists — no point retrying
      if (status === 401 || status === 403 || status === 404 || status === 410) lsRemove(LS_KEYS.pendingLeave)
      // Any other error (network, 5xx) — leave it for next app load to retry
    }
  },

  handleRemoved: function({ commit, dispatch }) {
    dispatch('clearGroupOrder')
    commit('SET_REMOVED', true)
  },

  handleCancelled: function({ commit, dispatch }) {
    dispatch('clearGroupOrder')
    commit('SET_CANCELLED', true)
  },
}

export default {
  namespaced: true,
  state: state,
  getters: getters,
  mutations: mutations,
  actions: actions,
}
